// ===============================================================
// Forms — Public Form Page (NO auth required)
// Renders a published form for public submission at /forms/public/:slug
// ===============================================================

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, Stack, Loader, ThemeIcon, Button,
} from '../../components/gab/GabComponents';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { fetchTable, TABLES, FIELDS, createRecord } from '../../lib/api-switch';
import { FIELDS as RAW_FIELDS } from '../../api/fields';
import { FormRenderer } from './FormPreview';
import type { FormTemplate, FormSchema } from '../../types/forms';
import { primary } from '../../lib/colors';

// ── Helpers ─────────────────────────────────────────────────────

function parseTemplate(row: Record<string, unknown>): FormTemplate {
  const FT = FIELDS.formTemplates;
  const id = Number(row.record_id) || Number(row.id) || 0;
  const schema = String(row[FT.schema] ?? '{}');
  // isPublic/publicSlug stored inside schema JSON blob
  let isPublic = 'false';
  let publicSlug = '';
  try {
    const parsed = JSON.parse(schema);
    isPublic = (parsed.isPublic === true || parsed.isPublic === 'true') ? 'true' : 'false';
    publicSlug = String(parsed.publicSlug || '');
  } catch { /* ignore */ }
  return {
    id,
    Rid: id,
    templateName: String(row[FT.templateName] ?? ''),
    description: String(row[FT.description] ?? ''),
    schema,
    status: (String(row[FT.status] ?? 'draft')) as FormTemplate['status'],
    module: '',
    incidentId: '',
    isPublic,
    publicSlug,
    creatorName: String(row[FT.creatorName] ?? ''),
    responseCount: String(row[FT.responseCount] ?? '0'),
    datecreated: '',
  };
}

// ── Main Component ──────────────────────────────────────────────

export default function FormPublic() {
  const { slug } = useParams<{ slug: string }>();
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        // Fetch all templates (no auth token — anonymous proxy injects admin token)
        const data = await fetchTable(TABLES.formTemplates, { limit: 200, anonymous: true });
        const templates = data.map(parseTemplate);
        const match = templates.find(t =>
          t.publicSlug === slug && t.isPublic === 'true' && t.status === 'published'
        );
        if (!match) {
          setError('not_found');
          return;
        }
        setTemplate(match);
        try {
          const parsed = JSON.parse(match.schema);
          // Ensure settings object exists with defaults
          if (parsed && !parsed.settings) {
            parsed.settings = {
              submitButtonText: 'Submit',
              successMessage: 'Thank you! Your response has been recorded.',
              allowMultipleSubmissions: false,
              notifyOnSubmission: false,
              notifyEmail: '',
            };
          }
          setSchema(parsed);
        } catch {
          setError('invalid_schema');
        }
      } catch (err) {
        setError('load_failed');
        console.error('Failed to load form:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // ── Submit handler ──────────────────────────────────────────
  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!template) return;
    setSubmitting(true);
    try {
      const respondentName = String(data.name || data.respondentName || data.full_name || data.f_full_name || data.f_first ? `${data.f_first || ''} ${data.f_last || ''}`.trim() : 'Anonymous');
      const respondentEmail = String(data.email || data.respondentEmail || data.f_email || '');
      const FR = RAW_FIELDS.formResponses;

      await createRecord(TABLES.formResponses, [
        [FR.templateId, String(template.Rid || template.id)],
        [FR.respondentName, respondentName],
        [FR.respondentEmail, respondentEmail],
        [FR.responseData, JSON.stringify(data)],
        [FR.status, 'new'],
        [FR.submittedAt, new Date().toISOString()],
      ], { anonymous: true });
      setSubmitted(true);
    } catch (err) {
      console.error('Submission failed:', err);
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Loader color="white" size="lg" />
      </Box>
    );
  }

  // ── Error states ────────────────────────────────────────────
  if (error) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={480} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="red">
              <IconAlertCircle size={36} />
            </ThemeIcon>
            <Title order={3} ta="center">
              {error === 'not_found' ? 'Form Not Found' : 'Unable to Load Form'}
            </Title>
            <Text ta="center" c="dimmed" size="sm">
              {error === 'not_found'
                ? 'This form does not exist, is no longer accepting responses, or the link may be incorrect.'
                : 'There was a problem loading this form. Please try again later.'}
            </Text>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Success ─────────────────────────────────────────────────
  if (submitted && schema) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={520} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="green">
              <IconCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">
              {schema.settings?.successMessage || 'Thank you! Your response has been recorded.'}
            </Title>
            <Text ta="center" c="dimmed" size="sm">
              Your submission has been received. You may close this page.
            </Text>
            {schema.settings?.allowMultipleSubmissions && (
              <Button
                variant="light"
                color={primary[700]}
                onClick={() => setSubmitted(false)}
                mt="sm"
              >
                Submit Another Response
              </Button>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ── Form ────────────────────────────────────────────────────
  if (!schema) return null;

  return (
    <Box mih="100vh" style={{
      background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
      padding: '40px 16px',
    }}>
      <Paper shadow="lg" radius="md" p="xl" maw={740} mx="auto" pos="relative">
        {submitting && (
          <Box
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{
              background: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-md)',
              zIndex: 10,
            }}
          >
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Submitting...</Text>
            </Stack>
          </Box>
        )}

        <FormRenderer schema={schema} onSubmit={handleSubmit} />
      </Paper>

      {/* Footer */}
      <Text ta="center" size="xs" c="rgba(255,255,255,0.5)" mt="lg">
        Powered by OpenGov Emergency Management
      </Text>
    </Box>
  );
}
